import { DATA_GRID_PLACEHOLDER, DataGridConfig } from 'components/DataGrid';
import history from 'history.js';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import callApi from 'lib/callApi';
import { truncateText } from 'lib/helper';
import { showFailureToast } from 'lib/ToastHelper';
import { useEffect, useMemo, useState } from 'react';
import RowActions from './RowActions';
import {
  activeEPColumnKeysAtom,
  agedOutEPColumnKeysAtom,
  checkedListAtom,
  DEFAULT_ACTIVE_EP_LISTING_COLUMNS,
  DEFAULT_AGED_OUT_EP_LISTING_COLUMNS,
  epDataQueryParamsAtom,
  epListingDataAtom,
  forceRerenderUIAtom,
  isQuerystringFiltersInitializedAtom,
  orderByAtom,
  paginationConfigAtom,
  sortOrderAtom,
} from './state';
import AgingOutLastDate from '../../components/AgingOutLastDate/AgingOutLastDate';
import { getDrillDownUrl } from '../../helper';
import { useEPListFilterValue } from '../hooks';
import { appliedEPIdsAtom, epRefreshAtom } from '../state';

export const useEPDataConfig = () => {
  const dataQueryParams = useAtomValue(epDataQueryParamsAtom);
  const isQuerystringFiltersInitialized = useAtomValue(
    isQuerystringFiltersInitializedAtom,
  );
  const [listingData, setListingData] = useAtom(epListingDataAtom);
  const [paginationConfig, setPaginationConfig] = useAtom(paginationConfigAtom);
  const [orderBy, setOrderBy] = useAtom(orderByAtom);
  const [sortOrder, setSortOrder] = useAtom(sortOrderAtom);
  const appliedRowIds = useAtomValue(appliedEPIdsAtom);
  const [selectedRowIds, setSelectedRowIds] = useAtom(checkedListAtom);
  const [activeEPColumnKeys, updateActiveEPColumnKeys] = useAtom(
    activeEPColumnKeysAtom,
  );
  const [agedOutEPColumnKeys, updateAgedOutEPColumnKeys] = useAtom(
    agedOutEPColumnKeysAtom,
  );
  const forceRerenderUI = useAtomValue(forceRerenderUIAtom);
  const [loading, setLoading] = useState(false);
  const setEPRefresh = useSetAtom(epRefreshAtom);
  const enableSelectionAcrossPages = true;
  const selectedEndpointsFilter = useEPListFilterValue<
    'active' | 'aging' | 'deleted'
  >('filter');

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      if (dataQueryParams === null) return;
      try {
        setListingData({ ...listingData, loading: true });
        const responseData = await callApi(
          `/endpoints/`,
          'GET',
          dataQueryParams,
        );
        setListingData({
          loading: false,
          items: responseData.data.results,
          count: responseData.data.count,
          insights: responseData.data.insights,
        });
      } catch (error) {
        const errorData = (error as any)?.data;
        const errorMessage = errorData
          ? `${Object.keys(errorData)[0]}: ${
              Object.values(errorData).flat()[0]
            }`
          : 'Failed to load data.';
        showFailureToast(errorMessage);
        setListingData({
          items: [],
          count: 0,
          loading: false,
          insights: {
            severity_breakdown: {
              urgent: 0,
              critical: 0,
              serious: 0,
              medium: 0,
              minimal: 0,
            },
            status_breakdown: {
              active: 0,
              aging: 0,
              deleted: 0,
            },
            total_vulnerabilities: 0,
            internal_count: 0,
            external_count: 0,
            aws_count: 0,
            unique_os_count: 0,
          },
        });
      } finally {
        setLoading(false);
      }
    };

    if (isQuerystringFiltersInitialized) {
      fetchData();
    }
  }, [dataQueryParams, isQuerystringFiltersInitialized]);

  const isEndpointAgingOut = (rowData) => {
    const checkedInDate = new Date(
      rowData['last_vm_scanned_date'] || rowData['scan__launch_datetime'],
    );
    const now = new Date();

    // Calculate the date 10 days ago from now
    const tenDaysAgo = new Date();
    tenDaysAgo.setDate(now.getDate() - 10);

    // // Calculate the date 30 days ago from now
    // const thirtyDaysAgo = new Date();
    // thirtyDaysAgo.setDate(now.getDate() - 30);
    // Check if the date is more than 10 days old but not more than 30 days old
    // return checkedInDate < tenDaysAgo && checkedInDate >= thirtyDaysAgo;
    return checkedInDate < tenDaysAgo;
  };

  const isDeletedFilterSelected = () => {
    return selectedEndpointsFilter === 'deleted';
  };
  const getColumnsDisplayConfig = () => {
    if (selectedEndpointsFilter === 'deleted') {
      return {
        enabledColumnKeys: agedOutEPColumnKeys,
        defaultColumnKeys: DEFAULT_AGED_OUT_EP_LISTING_COLUMNS,
        keysToBeExcluded: [
          'total_vuln',
          'new_vuln',
          'last_vuln_scan',
          'last_checked_in',
          'vuln',
        ],
        onColumnsDisplayChange: (updatedColumnKeys) => {
          updateAgedOutEPColumnKeys(updatedColumnKeys);
        },
      };
    } else {
      return {
        enabledColumnKeys: activeEPColumnKeys,
        defaultColumnKeys: DEFAULT_ACTIVE_EP_LISTING_COLUMNS,
        keysToBeExcluded: [
          'deleted_endpoint__open_vulnerability_count',
          'deleted_endpoint__closed_vulnerability_count',
          'deleted_endpoint__deleted_on',
          'deleted_endpoint__deleted_by',
        ],
        onColumnsDisplayChange: (updatedColumnKeys) => {
          updateActiveEPColumnKeys(updatedColumnKeys);
        },
      };
    }
  };

  const getHostIcon = (scannerType) => {
    const ExternalIcon = (
      <div
        className="fa-stack-lg d-flex align-items-center"
        style={{ position: 'relative', color: '#0e74bc' }}
      >
        <i
          className="fa fa fa-desktop fa-stack-lg fa-lg"
          style={{ color: '#0e74bc', fontSize: '20px' }}
        />
        <small
          style={{
            verticalAlign: 'top',
            marginLeft: '4px',
            marginBottom: '5px',
            position: 'absolute',
            fontWeight: 'bold',
            color: '#0e74bc',
            fontSize: '12px',
          }}
        >
          Ex
        </small>
      </div>
    );

    const InternalIcon = (
      <div
        className="fa-stack-lg d-flex align-items-center"
        style={{ position: 'relative', color: '#0e74bc' }}
      >
        <i
          className="fa fa fa-desktop fa-stack-lg fa-lg"
          style={{ color: '#0e74bc', fontSize: '20px' }}
        />
        <small
          style={{
            verticalAlign: 'top',
            marginLeft: '5px',
            marginBottom: '5px',
            position: 'absolute',
            fontWeight: 'bold',
            color: '#0e74bc',
            fontSize: '12px',
          }}
        >
          In
        </small>
      </div>
    );

    const AwsIcon = (
      <span
        className="fa-stack-lg"
        style={{ color: '#0e74bc', fontSize: '16px' }}
      >
        <i className="fa-brands fa-aws fa-lg"></i>
      </span>
    );

    switch (scannerType) {
      case 'External':
        return ExternalIcon;
      case 'Internal':
        return InternalIcon;
      case 'EC2':
        return AwsIcon;
      default:
        return ExternalIcon;
    }
  };

  const getScannerType = (scannerType) => {
    if (scannerType === 'EC2') return 'AWS';
    return scannerType;
  };

  const getGridConfig = (): DataGridConfig => {
    const config: DataGridConfig = {
      rowIdKey: 'id',
      columns: [
        {
          key: 'id',
          label: 'Id',
          sortable: true,
          headerTooltip: 'Row identifier in SP360 database',
        },
        {
          key: 'host_id',
          label: 'Qualys ID',
          sortable: true,
          headerTooltip: 'Host ID at Qualys',
          overrideContents: (rowData) =>
            rowData['host_id'] ? (
              rowData['host_id']
            ) : (
              <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                <span>{rowData['id']}</span>
                <i className="fas fa-exclamation-triangle fa-lg cursor-pointer" />
              </div>
            ),
          tooltip: (rowData) =>
            !rowData['host_id'] &&
            'Displaying Endpoint ID since Host ID is unavailable.',
        },
        {
          key: 'ip',
          label: 'IP Address',
          sortable: true,
          headerTooltip: 'Host IP address at Qualys',
        },
        {
          key: 'ip_with_type_and_alias',
          label: 'Host Info',
          sortable: true,
          orderBy: 'ip',
          rowCellStyle: {
            fontWeight: 'bold',
            width: 'auto',
            textDecoration: 'none',
          },
          headerCellStyle: { width: 'auto' },
          overrideContents: (rowData) => {
            const alias = rowData['alias'];
            const assetName = rowData['asset_name'];
            const ip = rowData['ip'];
            const displayName = alias || assetName || ip;
            return (
              <div>
                <div className="d-flex align-items-center">
                  <i
                    style={{
                      minWidth: '26px',
                      marginRight: '8px',
                    }}
                  >
                    {getHostIcon(rowData['scanner_type'])}
                  </i>
                  <div
                    style={{
                      textDecoration: 'underline',
                      textDecorationStyle: 'dotted',
                    }}
                  >
                    <strong>{truncateText(displayName, 45)}</strong>
                    {alias ? (
                      <div className="small">
                        {assetName && alias !== assetName
                          ? `${truncateText(ip, 30)} (${truncateText(
                              assetName,
                              30,
                            )})`
                          : truncateText(ip, 42)}
                      </div>
                    ) : assetName ? (
                      <div className="small">
                        {`${truncateText(ip, 30)} ${
                          displayName !== assetName
                            ? `(${truncateText(assetName, 30)})`
                            : ''
                        }`}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          },
          tooltip: (rowData) => {
            const alias = rowData['alias'];
            const assetName = rowData['asset_name'];
            const ip = rowData['ip'];

            return (
              <div>
                <div className="d-flex align-items-center">
                  <strong>Scanner Type:&nbsp;&nbsp;</strong>
                  {getHostIcon(rowData['scanner_type'])}
                </div>
                <div className="d-flex align-items-center">
                  <strong>Host IP Address:&nbsp;&nbsp;</strong> {ip}
                </div>
                {alias && (
                  <div className="d-flex align-items-center">
                    <strong>Alias:&nbsp;&nbsp;</strong>
                    {alias}
                  </div>
                )}
                {assetName && (
                  <div className="d-flex align-items-center">
                    <strong>Asset Name:&nbsp;&nbsp;</strong>
                    {assetName}
                  </div>
                )}
              </div>
            );
          },
          headerTooltip:
            'Composite of Host Name, Type and Alias or AssetName (primary if available)',
        },
        {
          key: 'ip_with_type',
          label: 'Host',
          sortable: true,
          orderBy: 'ip',
          rowCellStyle: {
            fontWeight: 'bold',
            width: 'auto',
            textDecoration: 'none',
          },
          headerCellStyle: { width: 'auto' },
          overrideContents: (rowData) => {
            return (
              <div>
                <div className="d-flex align-items-center">
                  <i
                    style={{
                      minWidth: '26px',
                      marginRight: '8px',
                    }}
                  >
                    {getHostIcon(rowData['scanner_type'])}
                  </i>
                  <div
                    style={{
                      textDecoration: 'underline',
                      textDecorationStyle: 'dotted',
                    }}
                  >
                    <strong>{truncateText(rowData.ip, 45)}</strong>
                  </div>
                </div>
              </div>
            );
          },
          tooltip: (rowData) => {
            const alias = rowData['alias'];
            const assetName = rowData['asset_name'];
            const ip = rowData['ip'];

            return (
              <div>
                <div className="d-flex align-items-center">
                  <strong>Scanner Type:&nbsp;&nbsp;</strong>
                  {getHostIcon(rowData['scanner_type'])}
                </div>
                <div className="d-flex align-items-center">
                  <strong>Host IP Address:&nbsp;&nbsp;</strong> {ip}
                </div>
                {alias && (
                  <div className="d-flex align-items-center">
                    <strong>Alias:&nbsp;&nbsp;</strong>
                    {alias}
                  </div>
                )}
                {assetName && (
                  <div className="d-flex align-items-center">
                    <strong>Asset Name:&nbsp;&nbsp;</strong>
                    {assetName}
                  </div>
                )}
              </div>
            );
          },
          headerTooltip: 'Host type and IP address',
        },
        {
          key: 'scanner_type',
          label: 'Scanner Type',
          sortable: true,
          headerTooltip: 'Scanner type (Internal, External or AWS)',
          overrideContents: (rowData) =>
            getScannerType(rowData['scanner_type']),
        },
        {
          key: 'asset_name',
          label: 'Asset Name',
          sortable: true,
          headerTooltip: 'Asset name at Qualys',
          overrideContents: (rowData) =>
            rowData.asset_name !== rowData.ip ? (
              rowData.asset_name
            ) : (
              <>{DATA_GRID_PLACEHOLDER}</>
            ),
        },
        {
          key: 'alias',
          label: 'Alias',
          sortable: true,
          headerTooltip: 'Custom alias of the endpoint',
        },
        {
          key: 'ca_created',
          label: 'Created At',
          sortable: true,
          headerTooltip: 'Created date of the endpoint',
        },
        {
          key: 'ca_modified',
          label: 'Updated At',
          sortable: true,
          headerTooltip: 'Last updated date of the endpoint',
        },
        {
          key: 'scan__launch_datetime',
          label: 'Last Scanned At',
          sortable: true,
          headerTooltip: 'Last scanned date of the endpoint',
          render: (rowData) => (
            <AgingOutLastDate
              lastDate={
                rowData['last_vm_scanned_date'] ||
                rowData['scan__launch_datetime']
              }
              totalDays={1 * 30}
              dangerDaysDiff={10}
              warningDaysDiff={20}
              tooltipTitle="Endpoint is inactive and will age out"
            />
          ),
        },
        {
          key: 'vuln',
          label: 'Vuln Count',
          sortable: true,
          headerTooltip: 'Total number of vulnerabilities',
          overrideContents: (rowData) => (
            <label
              className="label btn slim"
              style={{ color: 'rgb(255, 255, 255)', cursor: 'pointer' }}
            >
              {rowData.vuln}
            </label>
          ),
          onClick: (rowData) =>
            history.push(
              getDrillDownUrl({
                type: 'open',
                sourceType: 'endpoint_data',
                hosts_in: [rowData.id],
                severity: '',
                issueType: '',
                qid: '',
                severity_in: undefined,
              }),
              // `/${BASE_URL}/qualys/vulnerabilities?source_type=endpoint_data&endpoint_id=${rowData.id}`,
            ),
        },
        {
          key: 'new_vuln',
          label: 'New Vuln Count',
          sortable: true,
          headerTooltip: 'Total number of new vulnerabilities',
          overrideContents: (rowData) => (
            <label
              className="label btn slim"
              style={{ color: 'rgb(255, 255, 255)', cursor: 'pointer' }}
            >
              {rowData.new_vuln}
            </label>
          ),
        },
        {
          key: 'deleted_endpoint__open_vulnerability_count',
          label: 'Open Vuln Count',
          sortable: true,
          headerTooltip: 'Number of open vulnerabilities',
          overrideContents: (rowData) => (
            <label
              className="label btn slim"
              style={{ color: 'rgb(255, 255, 255)', cursor: 'pointer' }}
            >
              {rowData.deleted_endpoint__open_vulnerability_count}
            </label>
          ),
        },
        {
          key: 'deleted_endpoint__closed_vulnerability_count',
          label: 'Closed Vuln Count',
          sortable: true,
          headerTooltip: 'Number of closed vulnerabilities',
          overrideContents: (rowData) => (
            <label
              className="label btn slim"
              style={{ color: 'rgb(255, 255, 255)', cursor: 'pointer' }}
            >
              {rowData.deleted_endpoint__closed_vulnerability_count}
            </label>
          ),
        },
        {
          key: 'deleted_endpoint__deleted_on',
          label: 'Deleted At',
          sortable: true,
          headerTooltip: 'Endpoint deletion date',
        },
        {
          key: 'deleted_endpoint__deleted_by',
          label: 'Deleted By',
          sortable: true,
          headerTooltip: 'User who deleted the endpoint',
        },
        {
          key: 'actions',
          label: 'Actions',
          render: (rowData) => (
            <RowActions rowData={rowData} onLoading={setLoading} />
          ),
          headerCellStyle: {
            textAlign: 'right',
            maxWidth: 'auto',
            minWidth: '80px !important',
          },
          rowCellStyle: {
            textAlign: 'right',
            minWidth: '80px !important',
            maxWidth: 'auto',
          },
        },
      ],
      columnsDisplayConfig: getColumnsDisplayConfig(),
      rowConfig: {
        classRules: [
          // {
          //   className: "new-row",
          //   ruleFunction: (rowData) => isWithinLastMinute(rowData["created_at"]),
          // },
          {
            className: 'warning-row',
            ruleFunction: isEndpointAgingOut,
          },
          {
            className: 'danger-row',
            ruleFunction: isDeletedFilterSelected,
          },
          {
            className: 'applied-row',
            ruleFunction: (rowData) =>
              rowData['isAppliedForDashboard'] === true,
          },
        ],
      },
      paginationConfig: {
        total_count: listingData?.count || 0,
        pageNumber: paginationConfig.page - 1, // page number expects a zero based index
        pageSize: paginationConfig.page_size,
        onPageChange: (newPage: number) => {
          // console.log("page number changed to " + newPage);
          // page number returns a zero based index, so incrementing by 1
          setPaginationConfig({
            page_size: paginationConfig.page_size,
            page: newPage + 1,
          });
          if (!enableSelectionAcrossPages) {
            // reset selected row ids on  change
            setSelectedRowIds([]);
          }
        },
        onPageSizeChange: (newPageSize: number) => {
          // console.log("page size changed to " + newPageSize);
          // reset page number on page size change
          setPaginationConfig({ page_size: newPageSize, page: 1 });
          // reset selected row ids on  change
          setSelectedRowIds([]);
        },
      },
      sortingConfig: {
        orderBy: orderBy,
        order: sortOrder,
        onOrderByChange: (value: string) => setOrderBy(value),
        onOrderChange: (value) => setSortOrder(value),
      },
      //   TODO: add more configuration options for responsive columns display, for different screen sizes
    };
    // Conditionally add row selection configuration, should not be available for deleted EP's
    config.rowSelectionConfig = isDeletedFilterSelected()
      ? undefined
      : {
          enableSelectionAcrossPages: enableSelectionAcrossPages,
          preSelectedRows: selectedRowIds,
          onSelectionChange: (updatedRowIds) => {
            setSelectedRowIds(updatedRowIds);
          },
        };
    return config;
  };

  const config = useMemo<DataGridConfig>(() => {
    return getGridConfig();
  }, [
    listingData,
    activeEPColumnKeys,
    agedOutEPColumnKeys,
    setEPRefresh,
    forceRerenderUI,
    paginationConfig,
    selectedRowIds,
    appliedRowIds,
    selectedEndpointsFilter,
  ]);

  return {
    config,
    data: listingData?.items,
    loading,
  };
};
